import { AIApi } from "../api/aiApi";
import { medicalsApi } from "../api/medicalsApi";
import CodingSystems from "../constants/CodingSystems";
import { QueryKeys } from "../constants/queryKeys";
import { queryClient } from "../queryClient";
import { CodeableConcept } from "../types/codeableConcept";
import {
  TCopilotNoteAllergy,
  TCopilotPhysicalAssessment,
  TCopilotPrescription,
  TCopilotReviewOfSystems,
  TCopilotSexualHistory,
  TCopilotSocialHistory,
} from "../types/copilot";
import { Reference } from "../types/reference";
import {
  CopilotSourceSection,
  withCopilotSource,
  withCopilotSourceOnItem,
} from "../pages/health/encounters/components/Copilot/utils/copilotSourceExtensions";
import { SocialHistoryQuestionnaire } from "../types/socialHistory";
import {
  createEncounterParam,
  createNote,
  createReference,
} from "../utils/DataTransformationUtils";
import { createCodeableConcept } from "../utils/DataUtils";
import { AllergiesManager } from "./AllergiesManager";
import { CarePlansManager } from "./CarePlansManager";
import { ChiefComplaintManager } from "./ChiefComplaintManager";
import { ImmunizationsManager } from "./ImmunizationsManager";
import { MedicalHistoryManager } from "./MedicalHistoryManager";
import { MedicationRequestManager } from "./MedicationRequestManager";
import { MedicationsManager } from "./MedicationsManager";
import { PhysicalAssessmentManager } from "./PhysicalAssessmentManager";
import { ProceduresManager } from "./ProceduresManager";
import { ReviewOfSystemsManager } from "./ReviewOfSystemsManager";
import { SexualHistoryManager } from "./SexualHistoryManager";
import { SocialHistoryManager } from "./SocialHistoryManager";

// export class CopilotManager {
const generateNote = async (copilotId: string, countryCode: string) => {
  const response = await new AIApi().generateNote(copilotId, countryCode);
  return response.data;
};

const populateAllergies = async (
  encounterId: string,
  organization: string,
  countryCode?: string,
  options?: { overwriteReactions?: boolean },
) => {
  try {
    const response = await new AIApi().populateAllergies(
      encounterId,
      organization,
      countryCode,
      options,
    );

    AllergiesManager.invalidate();
    return response.data;
  } catch (error) {
    console.error("Error populating allergies:", error);
    throw error;
  }
};

const populateFamilyHistory = async (
  encounterId: string,
  organization: string,
  countryCode?: string,
) => {
  try {
    const response = await new AIApi().populateFamilyHistory(
      encounterId,
      organization,
      countryCode,
    );
    // History cards refresh via copilot_refresh_history callback
    return response.data;
  } catch (error) {
    console.error("Error populating family history:", error);
    throw error;
  }
};

const populateHospitalization = async (
  encounterId: string,
  organization: string,
  countryCode?: string,
) => {
  try {
    const response = await new AIApi().populateHospitalizations(
      encounterId,
      organization,
      countryCode,
    );
    // History cards refresh via copilot_refresh_history callback
    return response.data;
  } catch (error) {
    console.error("Error populating hospitalizations:", error);
    throw error;
  }
};

// TODO: send data to API
const populatePhysicalAssessment = async (
  physicalAssessment: TCopilotPhysicalAssessment,
  patientReference: Reference,
  encounterReference: Reference,
  practitionerReference: Reference,
  copilotId?: string,
) => {
  const section: CopilotSourceSection = "physical-assessment";

  const currentAssessment =
    await PhysicalAssessmentManager.findPhysicalAssessment(
      encounterReference,
      patientReference,
    );

  const assessmentQuestionnaire =
    await PhysicalAssessmentManager.findQuestionnaire();

  const copilotAssessmentItems = Object.entries(physicalAssessment).map(
    ([key, value]) => ({
      key,
      value,
    }),
  );

  const updateQuestionnaireItem = (
    item: { key: string; value: string },
    questionnaire: any,
  ) => {
    const note = createNote(item.value, practitionerReference);
    const updated = {
      ...questionnaire,
      note: [note],
    };

    return copilotId
      ? withCopilotSourceOnItem(updated, copilotId, section, item.key)
      : updated;
  };

  if (currentAssessment) {
    copilotAssessmentItems.forEach((item) => {
      const itemIndex = currentAssessment.item.findIndex((questionnaireItem) =>
        questionnaireItem?.text
          ?.toLowerCase()
          ?.includes(item.key.toLowerCase()),
      );

      if (itemIndex !== -1) {
        currentAssessment.item[itemIndex] = updateQuestionnaireItem(
          item,
          currentAssessment.item[itemIndex],
        );
      } else {
        const questionnaireIndex = assessmentQuestionnaire.item.findIndex(
          (questionnaireItem) =>
            questionnaireItem.text
              .toLowerCase()
              .includes(item.key.toLowerCase()),
        );

        if (questionnaireIndex !== -1) {
          currentAssessment.item[questionnaireIndex] = updateQuestionnaireItem(
            item,
            assessmentQuestionnaire.item[questionnaireIndex],
          );
        }
      }
    });

    const payload = copilotId
      ? withCopilotSource(currentAssessment, copilotId, section)
      : currentAssessment;

    await medicalsApi.updateEntity(
      currentAssessment.id,
      payload,
      "questionnaire-response",
      createEncounterParam(encounterReference),
    );
    return;
  }

  copilotAssessmentItems.forEach((item) => {
    const questionnaireItem = assessmentQuestionnaire.item.find((qi) =>
      qi.text.toLowerCase().includes(item.key.toLowerCase()),
    );

    if (questionnaireItem) {
      questionnaireItem.note = [createNote(item.value, practitionerReference)];
      if (copilotId) {
        Object.assign(
          questionnaireItem,
          withCopilotSourceOnItem(
            questionnaireItem,
            copilotId,
            section,
            item.key,
          ),
        );
      }
    }
  });

  const assessment = PhysicalAssessmentManager.createDefault(
    assessmentQuestionnaire,
    encounterReference,
    patientReference,
    new Date(),
    practitionerReference,
  );
  const payload = copilotId
    ? withCopilotSource(assessment, copilotId, section)
    : assessment;

  await medicalsApi.createEntity(
    payload,
    "questionnaire-response",
    createEncounterParam(encounterReference),
  );
};

const populateReviewOfSystems = async (
  reviewOfSystems: TCopilotReviewOfSystems,
  patientReference: Reference,
  encounterReference: Reference,
  practitionerReference: Reference,
  copilotId?: string,
) => {
  const section: CopilotSourceSection = "review-of-systems";

  const currentReview = await ReviewOfSystemsManager.findReviewOfSystems(
    encounterReference,
    patientReference,
  );

  const reviewQuestionnaire = await ReviewOfSystemsManager.findQuestionnaire();

  const copilotReviewItems = Object.entries(reviewOfSystems).map(
    ([key, value]) => ({
      key,
      value,
    }),
  );

  const updateQuestionnaireItem = (
    item: { key: string; value: string },
    questionnaire: any,
  ) => {
    const note = createNote(item.value, practitionerReference);

    const updated = {
      ...questionnaire,
      note: [note],
    };

    return copilotId
      ? withCopilotSourceOnItem(updated, copilotId, section, item.key)
      : updated;
  };

  if (currentReview) {
    copilotReviewItems.forEach((item) => {
      const itemIndex = currentReview.item.findIndex((questionnaireItem) =>
        questionnaireItem?.text
          ?.toLowerCase()
          ?.includes(item.key.toLowerCase()),
      );

      if (itemIndex !== -1) {
        currentReview.item[itemIndex] = updateQuestionnaireItem(
          item,
          currentReview.item[itemIndex],
        );
      } else {
        const questionnaireIndex = reviewQuestionnaire.item.findIndex(
          (questionnaireItem) =>
            questionnaireItem.text
              .toLowerCase()
              .includes(item.key.toLowerCase()),
        );

        if (questionnaireIndex !== -1) {
          currentReview.item[questionnaireIndex] = updateQuestionnaireItem(
            item,
            reviewQuestionnaire.item[questionnaireIndex],
          );
        }
      }
    });

    const payload = copilotId
      ? withCopilotSource(currentReview, copilotId, section)
      : currentReview;

    await medicalsApi.updateEntity(
      currentReview.id,
      payload,
      "questionnaire-response",
      createEncounterParam(encounterReference),
    );
    return;
  }
  copilotReviewItems.forEach((item) => {
    const questionnaireItem = reviewQuestionnaire.item.find((qi) =>
      qi.text.toLowerCase().includes(item.key.toLowerCase()),
    );

    if (questionnaireItem) {
      questionnaireItem.note = [createNote(item.value, practitionerReference)];
      if (copilotId) {
        Object.assign(
          questionnaireItem,
          withCopilotSourceOnItem(
            questionnaireItem,
            copilotId,
            section,
            item.key,
          ),
        );
      }
    }
  });

  const review = ReviewOfSystemsManager.createDefault(
    reviewQuestionnaire,
    encounterReference,
    patientReference,
    new Date(),
    practitionerReference,
  );
  const payload = copilotId
    ? withCopilotSource(review, copilotId, section)
    : review;

  await medicalsApi.createEntity(
    payload,
    "questionnaire-response",
    createEncounterParam(encounterReference),
  );
};

const populateMedicalHistory = async (
  encounterId: string,
  organization: string,
  countryCode?: string,
) => {
  try {
    const response = await new AIApi().populateMedicalHistory(
      encounterId,
      organization,
      countryCode,
    );
    // History cards refresh via copilot_refresh_history callback
    return response.data;
  } catch (error) {
    console.error("Error populating medical history:", error);
    throw error;
  }
};

const populateProcedures = async (
  encounterId: string,
  organization: string,
  countryCode?: string,
) => {
  try {
    const response = await new AIApi().populateProcedures(
      encounterId,
      organization,
      countryCode,
    );
    return response.data;
  } catch (error) {
    console.error("Error populating procedures:", error);
    throw error;
  }
};

const populateMedications = async (
  encounterId: string,
  organization: string,
  countryCode?: string,
) => {
  try {
    const response = await new AIApi().populateMedications(
      encounterId,
      organization,
      countryCode,
    );
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [QueryKeys.MEDICATION] }),
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MEDICATION_STATEMENT],
      }),
    ]);
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error populating medications:", error);
    throw error;
  }
};

const populateImmunizations = async (
  encounterId: string,
  organization: string,
  countryCode?: string,
) => {
  try {
    const response = await new AIApi().populateImmunizations(
      encounterId,
      organization,
      countryCode,
    );
    return response.data;
  } catch (error) {
    console.error("Error populating immunizations:", error);
    throw error;
  }
};

const populateDiagnoses = async (
  encounterId: string,
  organization: string,
  countryCode?: string,
) => {
  try {
    const response = await new AIApi().populateDiagnoses(
      encounterId,
      organization,
      countryCode,
    );
    return response.data;
  } catch (error) {
    console.error("Error populating diagnoses:", error);
    throw error;
  }
};

const populateCarePlans = async (
  encounterId: string,
  organization: string,
  countryCode?: string,
) => {
  try {
    const response = await new AIApi().populateCarePlans(
      encounterId,
      organization,
      countryCode,
    );
    CarePlansManager.invalidate();
    return response.data;
  } catch (error) {
    console.error("Error populating care plans:", error);
    throw error;
  }
};

const populatePrescriptions = async (
  prescriptions: TCopilotPrescription[],
  patientReference: Reference,
  encounterReference: Reference,
  practitionerReference: Reference,
  copilotId?: string,
) => {
  const prescriptionsItems = prescriptions.map((prescription) => {
    const item = MedicationRequestManager.createDefault(
      {
        ...createReference(
          "medications",
          `other-${prescription.medicationName}`,
          prescription.medicationName,
        ),
        info: {
          ingredient: [],
        },
      },
      encounterReference,
      patientReference,
      practitionerReference,
    );

    return copilotId
      ? withCopilotSource(item, copilotId, "prescriptions")
      : item;
  });
  const promises = prescriptionsItems.map((item) =>
    medicalsApi.createEntity(
      item,
      "medication-requests",
      createEncounterParam(encounterReference),
    ),
  );
  await Promise.all(promises);
};

const populateSocialHistory = async (
  socialHistory: TCopilotSocialHistory,
  patientReference: Reference,
  encounterReference: Reference,
  practitionerReference: Reference,
  copilotId?: string,
) => {
  const section: CopilotSourceSection = "social-history";
  const currentSocialHistory = await SocialHistoryManager.findSocialHistory(
    encounterReference,
    patientReference,
  );

  const questionTextToKeyMap = {
    "What is your occupation?": socialHistory.occupation?.current,
    "What type of accommodation do you live in?":
      socialHistory.livingSituation?.accommodationType,
    "How many members live in your household?":
      socialHistory.livingSituation?.householdMembers,
    "Do you smoke?": socialHistory.smokeUse?.status,
    "What have you or do you smoke?": socialHistory.smokeUse?.details?.type,
    "Packs of cigarrettes smoked per day? (1 pack = 20 cigarettes)":
      socialHistory.smokeUse?.details?.packsPerDay,
    "Years Smoked": socialHistory.smokeUse?.details?.yearsSmoked,
    "Pack Years": socialHistory.smokeUse?.details?.packYears,
    "When did you quit?": socialHistory.smokeUse?.details?.quitDate,
    "Do you drink alcohol?": socialHistory.alcoholUse?.status,
    "How often?": socialHistory.alcoholUse?.details?.period,
    "Number of drinks per time period (day, week, month, year)?":
      socialHistory.alcoholUse?.details?.drinksPerPeriod,
    "Type of drinks?": socialHistory.alcoholUse?.details?.types,
    "Exercise Frequency?": socialHistory.exercise?.frequency,
  };

  function injectAnswers(items, questionTextToKeyMap) {
    if (!Array.isArray(items)) return items;
    return items.map((item) => {
      const newItem = { ...item };
      if (newItem.text && questionTextToKeyMap.hasOwnProperty(newItem.text)) {
        newItem.answer = questionTextToKeyMap[newItem.text];
      }

      if (newItem.item) {
        newItem.item = injectAnswers(newItem.item, questionTextToKeyMap);
      }

      return newItem;
    });
  }

  if (currentSocialHistory) {
    // update the questionnaire with the answers
    const updatedQuestionnaire = injectAnswers(
      currentSocialHistory.item,
      questionTextToKeyMap,
    );
    currentSocialHistory.item = updatedQuestionnaire;
    const payload = copilotId
      ? withCopilotSource(currentSocialHistory, copilotId, section)
      : currentSocialHistory;
    await medicalsApi.updateEntity(
      currentSocialHistory.id,
      payload,
      "questionnaire-response",
      createEncounterParam(encounterReference),
    );
    return;
  }

  //create new questionnaire response
  const questionnaire: SocialHistoryQuestionnaire =
    await SocialHistoryManager.findQuestionnaire();

  const newSocialHistory = SocialHistoryManager.createDefault(
    questionnaire,
    patientReference,
    encounterReference,
    new Date(),
    practitionerReference,
  );

  const updatedQuestionnaire = injectAnswers(
    newSocialHistory.item,
    questionTextToKeyMap,
  );

  newSocialHistory.item = updatedQuestionnaire;

  const payload = copilotId
    ? withCopilotSource(newSocialHistory, copilotId, section)
    : newSocialHistory;

  await medicalsApi.createEntity(
    payload,
    "questionnaire-response",
    createEncounterParam(encounterReference),
  );
  return;
};

const populateSexualHistory = async (
  sexualHistory: TCopilotSexualHistory,
  patientReference: Reference,
  encounterReference: Reference,
  practitionerReference: Reference,
  copilotId?: string,
) => {
  const section: CopilotSourceSection = "sexual-history";
  const currentSexualHistory = await SexualHistoryManager.findSexualHistory(
    encounterReference,
    patientReference,
  );

  const questionTextToKeyMap = {
    "Have you ever been sexually active?": sexualHistory.activity?.everActive,
    "Are you currently sexually active?":
      sexualHistory.activity?.currentlyActive,
    "Sexual partners": sexualHistory.activity?.partnerGender,
    "What type of sexual activity do you engage in?":
      sexualHistory.activity?.activityTypes,
    "Do you use contraceptives?":
      sexualHistory.contraception?.usesContraception,
    "Methods of Contraception": sexualHistory.contraception?.methods,
    "Reason for not using contraception?":
      sexualHistory.contraception?.reasonForNoUse,
    "Are you satisfied with your current method of contraception?":
      sexualHistory.contraception?.satisfaction,
    "Are you interested in a different contraception method?":
      sexualHistory.contraception?.interestedInChange,
    "Have you ever used emergency contraception?":
      sexualHistory.contraception?.emergencyContraception,
    "What was your condom use in the last year?":
      sexualHistory.condomUse?.lastYear,
    "Were condoms used during your last sexual activity?":
      sexualHistory.condomUse?.lastActivity,
    "What type of condom was used?": sexualHistory.condomUse?.type,
    "HIV status": sexualHistory.stis?.hivStatus,
    "Have you ever been treated for HIV or any other STD(s)?":
      sexualHistory.stis?.previouslyTreatment,
    "Result if known": sexualHistory.stis?.treatmentResult,
    "Partner history of HIV or other STD(s)":
      sexualHistory.stis?.partnerHasHistory,
    "Has your current partner(s) ever been tested for HIV?":
      sexualHistory.stis?.partnerHasTested,
  };

  function injectAnswers(items, questionTextToKeyMap) {
    if (!Array.isArray(items)) return items;
    return items.map((item) => {
      const newItem = { ...item };
      if (newItem.text && questionTextToKeyMap.hasOwnProperty(newItem.text)) {
        newItem.answer = questionTextToKeyMap[newItem.text];
      }

      if (newItem.item) {
        newItem.item = injectAnswers(newItem.item, questionTextToKeyMap);
      }

      return newItem;
    });
  }

  if (currentSexualHistory) {
    // update the questionnaire with the answers
    const updatedQuestionnaire = injectAnswers(
      currentSexualHistory.item,
      questionTextToKeyMap,
    );
    currentSexualHistory.item = updatedQuestionnaire;
    const payload = copilotId
      ? withCopilotSource(currentSexualHistory, copilotId, section)
      : currentSexualHistory;
    await medicalsApi.updateEntity(
      currentSexualHistory.id,
      payload,
      "questionnaire-response",
      createEncounterParam(encounterReference),
    );
    return;
  }

  //create new questionnaire response
  const questionnaire = await SexualHistoryManager.findQuestionnaire();

  const newSexualHistory = SexualHistoryManager.createDefault(
    questionnaire,
    patientReference,
    encounterReference,
    new Date(),
    practitionerReference,
  );

  const updatedQuestionnaire = injectAnswers(
    newSexualHistory.item,
    questionTextToKeyMap,
  );

  newSexualHistory.item = updatedQuestionnaire;

  const payload = copilotId
    ? withCopilotSource(newSexualHistory, copilotId, section)
    : newSexualHistory;

  await medicalsApi.createEntity(
    payload,
    "questionnaire-response",
    createEncounterParam(encounterReference),
  );
  return;
};

const populateVitals = async (
  encounterId: string,
  organization: string,
  countryCode?: string,
) => {
  try {
    const response = await new AIApi().populateVitals(
      encounterId,
      organization,
      countryCode,
    );
    await queryClient.invalidateQueries({ queryKey: [QueryKeys.VITALS] });
    return response.data;
  } catch (error) {
    console.error("Error populating vitals:", error);
    throw error;
  }
};

const populateHpi = async (
  encounterId: string,
  organization: string,
  practitionerId: string,
  countryCode?: string,
) => {
  try {
    const response = await new AIApi().populateHpi(
      encounterId,
      organization,
      practitionerId,
      countryCode,
    );

    ChiefComplaintManager.invalidate();
    return response.data;
  } catch (error) {
    console.error("Error populating HPI:", error);
    throw error;
  }
};

const populateChiefComplaint = async (
  encounterId: string,
  organization: string,
  practitionerId: string,
  countryCode?: string,
) => {
  try {
    const response = await new AIApi().populateChiefComplaint(
      encounterId,
      organization,
      practitionerId,
      countryCode,
    );

    ChiefComplaintManager.invalidate();
    return response.data;
  } catch (error) {
    console.error("Error populating chief complaint:", error);
    throw error;
  }
};
// }

export const CopilotManager = {
  generateNote,
  populateAllergies,
  populateChiefComplaint,
  populateHpi,
  populateFamilyHistory,
  populateHospitalization,
  populatePhysicalAssessment,
  populateReviewOfSystems,
  populateMedicalHistory,
  populateProcedures,
  populateMedications,
  populateImmunizations,
  populateDiagnoses,
  populateCarePlans,
  populatePrescriptions,
  populateSocialHistory,
  populateSexualHistory,
  populateVitals,
};
